import create from 'zustand';
import { User, UserRole } from '../types/models';
import { authApi } from '../api/authApi';
import axiosInstance, { isAxiosError } from '../api/axios';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getUser: () => Promise<void>;
}

// Helper function to convert backend role values to frontend UserRole enum
const convertRolesToFrontendFormat = (roles: string[]): UserRole[] => {
  // The backend role values already match the UserRole enum
  return roles as UserRole[];
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      console.log('Attempting login with:', email);
      
      const response = await authApi.login({ username: email, password });

      console.log('Login response:', response);
      
      const { access_token, token_type } = response;
      const token = `${token_type} ${access_token}`;
      
      console.log('Setting token:', token);
      localStorage.setItem('token', token);
      
      // Set token in axios default headers
      axiosInstance.defaults.headers.common['Authorization'] = token;
      
      set({
        token,
        isAuthenticated: true,
        isLoading: false,
      });
      
      // Get user details after successful login
      await get().getUser();
    } catch (error) {
      console.error('Login error:', error);
      set({ isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    // Clear the authorization header
    delete axiosInstance.defaults.headers.common['Authorization'];
    set({ user: null, token: null, isAuthenticated: false });
  },

  getUser: async () => {
    // Check if we have a token first
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, cannot fetch user');
      return;
    }
    
    // Make sure the token is set in axios headers
    axiosInstance.defaults.headers.common['Authorization'] = token;
    
    set({ isLoading: true });
    try {
      console.log('Fetching user data from API...');
      const userData = await authApi.getUser();
      console.log('User data received:', userData);
      
      // Convert roles from backend format to frontend format if needed
      const user: User = {
        ...userData,
        roles: convertRolesToFrontendFormat(userData.roles || []),
      };
      
      console.log('Processed user data:', user);
      
      set({
        user,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      set({ isLoading: false });
      
      // If we can't get the user, logout
      if (isAxiosError(error) && error.response?.status === 401) {
        console.log('Unauthorized, logging out');
        get().logout();
      }
    }
  },
}));
